import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import './Home.css';

const Home = () => {
  const services = [
    {
      icon: 'bi-briefcase-fill',
      title: 'Consulting Services',
      description: 'Expert guidance to help you navigate complex challenges and make informed decisions.',
      color: 'bg-primary-teal',
    },
    {
      icon: 'bi-link-45deg',
      title: 'Resource Connections',
      description: 'Connect with vital community resources tailored to your unique needs and goals.',
      color: 'bg-primary-orange',
    },
    {
      icon: 'bi-person-workspace',
      title: 'Job Placement',
      description: 'Find meaningful employment opportunities that match your skills and aspirations.',
      color: 'bg-primary-purple',
    },
    {
      icon: 'bi-calculator-fill',
      title: 'Tax Services',
      description: 'Professional tax preparation and planning services to maximize your returns.',
      color: 'bg-primary-teal',
    },
  ];

  const features = [
    {
      icon: 'bi-people-fill',
      title: 'Community Focused',
      description: 'We prioritize the needs and growth of our community members.',
    },
    {
      icon: 'bi-award-fill',
      title: 'Expert Team',
      description: 'Experienced professionals dedicated to your success.',
    },
    {
      icon: 'bi-clock-history',
      title: '24/7 Support',
      description: 'Always available to assist you when you need us most.',
    },
    {
      icon: 'bi-graph-up-arrow',
      title: 'Proven Results',
      description: 'Track record of helping individuals and families thrive.',
    },
  ];

  const stats = [
    { number: '5000+', label: 'People Helped' },
    { number: '15+', label: 'Years Experience' },
    { number: '98%', label: 'Satisfaction Rate' },
    { number: '50+', label: 'Partner Organizations' },
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="c-hero-section position-relative overflow-hidden">
        <div className="container">
          <div className="row align-items-center min-vh-100 py-5">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="pe-lg-4"
              >
                <div className="c-badge-pill bg-primary-teal bg-opacity-10 text-primary-teal d-inline-flex align-items-center px-3 py-2 rounded-pill mb-4">
                  <i className="bi bi-star-fill me-2"></i>
                  <span className="fw-semibold small">Trusted by the community</span>
                </div>
                <h1 className="c-hero-title display-3 fw-bold mb-4 lh-1">
                  Bridging Communities,
                  <span className="text-primary-teal d-block mt-2">Building Futures</span>
                </h1>
                <p className="c-hero-description fs-5 text-muted lh-lg mb-4">
                  Empowering individuals and families through comprehensive support services.
                  We connect you with the resources, opportunities, and expertise you need to thrive.
                </p>
                <div className="c-hero-buttons d-flex flex-wrap gap-3 align-items-center">
                  <Link to="/services" className="btn btn-primary-teal btn-lg px-4 py-3 rounded-3 shadow-sm">
                    <i className="bi bi-grid-3x3-gap-fill me-2"></i>
                    Explore Services
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                  <Link to="/contact" className="btn btn-outline-primary btn-lg px-4 py-3 rounded-3">
                    <i className="bi bi-telephone-fill me-2"></i>
                    Get in Touch
                  </Link>
                </div>
                <div className="c-hero-stats d-flex flex-wrap gap-4 mt-5 pt-4 border-top border-2">
                  <div className="c-stat-item">
                    <h3 className="h2 fw-bold text-primary-teal mb-0">5000+</h3>
                    <p className="text-muted small mb-0">People Helped</p>
                  </div>
                  <div className="c-stat-item">
                    <h3 className="h2 fw-bold text-primary-orange mb-0">98%</h3>
                    <p className="text-muted small mb-0">Satisfaction</p>
                  </div>
                  <div className="c-stat-item">
                    <h3 className="h2 fw-bold text-primary-purple mb-0">15+</h3>
                    <p className="text-muted small mb-0">Years</p>
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="col-lg-6">
              <motion.div
                className="c-hero-visual"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="c-hero-card position-relative bg-white rounded-4 shadow-lg p-5 text-center">
                  <div className="c-card-gradient position-absolute top-0 start-0 w-100"></div>
                  <div className="position-relative">
                    <div className="c-icon-wrapper bg-gradient bg-primary-teal bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{width: '100px', height: '100px'}}>
                      <i className="bi bi-heart-pulse-fill text-primary-orange display-4"></i>
                    </div>
                    <h3 className="h4 fw-bold mb-3">Your Partner in Growth</h3>
                    <p className="text-muted mb-0">Connecting communities with opportunities and resources</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="c-services-section py-5 py-lg-6">
        <div className="container">
          <AnimatedSection className="text-center mb-5 pb-4">
            <div className="c-section-badge bg-primary-orange bg-opacity-10 text-primary-orange d-inline-flex align-items-center px-3 py-2 rounded-pill mb-3">
              <i className="bi bi-briefcase-fill me-2"></i>
              <span className="fw-semibold small">What We Offer</span>
            </div>
            <h2 className="display-5 fw-bold mb-3">Our Services</h2>
            <p className="fs-5 text-muted mb-0 mx-auto" style={{maxWidth: '600px'}}>
              Comprehensive solutions designed to support your journey
            </p>
          </AnimatedSection>

          <div className="row g-4">
            {services.map((service, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="c-service-card bg-white rounded-4 shadow-sm text-center p-4 h-100 border border-1 border-light"
                  whileHover={{ y: -8, boxShadow: '0 10px 40px rgba(0,0,0,0.12)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className={`c-card-icon ${service.color} rounded-4 d-inline-flex align-items-center justify-content-center mb-4`} style={{width: '80px', height: '80px'}}>
                    <i className={`${service.icon} fs-2 text-white`}></i>
                  </div>
                  <h4 className="h5 fw-bold mb-3">{service.title}</h4>
                  <p className="text-muted mb-4 lh-lg">{service.description}</p>
                  <Link to="/services" className="c-service-link text-decoration-none fw-semibold d-inline-flex align-items-center">
                    Learn More
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="c-stats-section py-5 py-lg-6 bg-light">
        <div className="container">
          <div className="row g-4 justify-content-center">
            {stats.map((stat, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="c-stat-card bg-white rounded-4 shadow-sm text-center p-4 border border-1 border-light h-100"
                  whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="c-stat-icon-wrapper bg-primary-teal bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '60px', height: '60px'}}>
                    <i className="bi bi-graph-up-arrow text-primary-teal fs-4"></i>
                  </div>
                  <h2 className="display-4 fw-bold text-primary-teal mb-2">{stat.number}</h2>
                  <p className="text-muted fw-medium mb-0">{stat.label}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="c-features-section py-5 py-lg-6">
        <div className="container">
          <AnimatedSection className="text-center mb-5 pb-4">
            <div className="c-section-badge bg-primary-purple bg-opacity-10 text-primary-purple d-inline-flex align-items-center px-3 py-2 rounded-pill mb-3">
              <i className="bi bi-award-fill me-2"></i>
              <span className="fw-semibold small">Our Advantages</span>
            </div>
            <h2 className="display-5 fw-bold mb-3">Why Choose Us</h2>
            <p className="fs-5 text-muted mb-0 mx-auto" style={{maxWidth: '600px'}}>
              We are committed to excellence in every interaction
            </p>
          </AnimatedSection>

          <div className="row g-4">
            {features.map((feature, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="c-feature-card bg-white rounded-4 shadow-sm text-center p-4 h-100 border border-1 border-light"
                  whileHover={{ y: -10, boxShadow: '0 15px 40px rgba(0,0,0,0.12)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="c-feature-icon-wrapper bg-gradient-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{width: '90px', height: '90px', background: 'linear-gradient(135deg, #299E81 0%, #8F49A7 100%)'}}>
                    <i className={`${feature.icon} text-white fs-2`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{feature.title}</h5>
                  <p className="text-muted mb-0 lh-lg">{feature.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="c-mission-section py-5 py-lg-6 position-relative overflow-hidden" style={{background: 'linear-gradient(135deg, #299E81 0%, #8F49A7 100%)'}}>
        <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)'}}></div>
        <div className="container position-relative">
          <div className="row align-items-center g-5">
            <AnimatedSection className="col-lg-6">
              <div className="c-section-badge bg-white bg-opacity-20 text-white d-inline-flex align-items-center px-3 py-2 rounded-pill mb-4">
                <i className="bi bi-bullseye me-2"></i>
                <span className="fw-semibold small">Our Purpose</span>
              </div>
              <h2 className="display-5 fw-bold text-white mb-4">Our Mission</h2>
              <p className="text-white fs-6 opacity-90 mb-4 lh-lg">
                At MWC Connections, we believe in the power of community and the potential within
                every individual. Our mission is to bridge gaps and create pathways to success through
                personalized support, expert guidance, and meaningful connections.
              </p>
              <p className="text-white fs-6 opacity-90 mb-4 lh-lg">
                We work tirelessly to ensure that everyone in our community has access to the resources
                and opportunities they need to build a better future for themselves and their families.
              </p>
              <Link to="/about" className="btn btn-light btn-lg px-4 py-3 rounded-3 fw-semibold shadow">
                <i className="bi bi-info-circle-fill me-2"></i>
                Learn More About Us
              </Link>
            </AnimatedSection>
            <AnimatedSection className="col-lg-6" delay={0.2}>
              <div className="c-mission-features d-flex flex-column gap-3">
                <motion.div
                  className="c-mission-item bg-white bg-opacity-15 backdrop-blur rounded-4 p-4 d-flex align-items-center gap-3 border border-white border-opacity-25"
                  whileHover={{ x: 10, backgroundColor: 'rgba(255,255,255,0.25)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="c-mission-icon bg-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{width: '50px', height: '50px'}}>
                    <i className="bi bi-check-circle-fill text-primary-teal fs-4"></i>
                  </div>
                  <span className="text-white fw-semibold fs-6">Personalized Support</span>
                </motion.div>
                <motion.div
                  className="c-mission-item bg-white bg-opacity-15 backdrop-blur rounded-4 p-4 d-flex align-items-center gap-3 border border-white border-opacity-25"
                  whileHover={{ x: 10, backgroundColor: 'rgba(255,255,255,0.25)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="c-mission-icon bg-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{width: '50px', height: '50px'}}>
                    <i className="bi bi-check-circle-fill text-primary-orange fs-4"></i>
                  </div>
                  <span className="text-white fw-semibold fs-6">Expert Guidance</span>
                </motion.div>
                <motion.div
                  className="c-mission-item bg-white bg-opacity-15 backdrop-blur rounded-4 p-4 d-flex align-items-center gap-3 border border-white border-opacity-25"
                  whileHover={{ x: 10, backgroundColor: 'rgba(255,255,255,0.25)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="c-mission-icon bg-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{width: '50px', height: '50px'}}>
                    <i className="bi bi-check-circle-fill text-primary-purple fs-4"></i>
                  </div>
                  <span className="text-white fw-semibold fs-6">Community Connections</span>
                </motion.div>
                <motion.div
                  className="c-mission-item bg-white bg-opacity-15 backdrop-blur rounded-4 p-4 d-flex align-items-center gap-3 border border-white border-opacity-25"
                  whileHover={{ x: 10, backgroundColor: 'rgba(255,255,255,0.25)' }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="c-mission-icon bg-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{width: '50px', height: '50px'}}>
                    <i className="bi bi-check-circle-fill text-success fs-4"></i>
                  </div>
                  <span className="text-white fw-semibold fs-6">Long-term Impact</span>
                </motion.div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="c-cta-section py-5 py-lg-6">
        <div className="container">
          <AnimatedSection>
            <div className="c-cta-wrapper bg-gradient rounded-5 shadow-lg p-5 text-center position-relative overflow-hidden" style={{background: 'linear-gradient(135deg, rgba(41, 158, 129, 0.95) 0%, rgba(143, 73, 167, 0.95) 100%)'}}>
              <div className="position-absolute top-0 end-0 opacity-10" style={{width: '300px', height: '300px', backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)'}}></div>
              <div className="position-relative">
                <div className="c-cta-icon bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center mb-4" style={{width: '80px', height: '80px'}}>
                  <i className="bi bi-rocket-takeoff-fill fs-2"></i>
                </div>
                <h2 className="display-5 fw-bold  mb-4">Ready to Get Started?</h2>
                <p className="fs-5  opacity-90 mb-5 mx-auto" style={{maxWidth: '600px'}}>
                  Let us help you connect with the resources and opportunities you need.
                  Reach out today to begin your journey.
                </p>
                <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center">
                  <Link to="/contact" className="btn btn-light btn-lg px-5 py-3 rounded-3 fw-semibold shadow-sm">
                    <i className="bi bi-envelope-fill me-2"></i>
                    Contact Us Today
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                  <Link to="/services" className="btn btn-outline-primary btn-lg px-5 py-3 rounded-3 fw-semibold">
                    <i className="bi bi-grid-3x3-gap-fill me-2"></i>
                    View Services
                  </Link>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
